/**
 * Browser-side CSV download, mirroring features/gdpr/gdpr-download.ts (the ONLY client-download pattern):
 * serialise → Blob → object URL → click a synthetic <a>. Purely client-side, no network — it runs only after
 * the report fetch has already resolved (online-only), so there is nothing to queue.
 *
 * RFC-4180: fields are comma-separated, CRLF row terminator; a field containing a comma, double-quote or
 * newline is wrapped in double-quotes with interior quotes doubled.
 *
 * FORMULA-INJECTION GUARD (load-bearing): a spreadsheet treats a cell beginning with `= + - @` (or a leading
 * TAB / CR) as a formula and will EXECUTE it on open — a classic CSV-injection vector. Every such cell is
 * prefixed with a single quote so the spreadsheet renders it as literal text. (£ amounts are safe — they
 * start with `£` — but a negative-pence figure rendered as "-1.50" WOULD trip the guard, which is correct.)
 *
 * MONEY CELLS — DECIDED ONCE, applied consistently: the caller passes money as a formatted £ STRING via
 * lib/format.ts money() (e.g. "£140.00"), NOT raw pence. The CSV is then a faithful, human-openable copy of
 * exactly what the report shows on screen (no hidden /100, no JS money math — see ADR-0009 money honesty).
 * Counts/rates/dates pass through as their displayed value.
 */

/** Quote one field per RFC-4180 + apply the formula-injection guard. */
function csvField(value: string | number): string {
  let s = String(value);
  // Formula-injection guard: neutralise a leading =, +, -, @, TAB or CR so no spreadsheet executes it.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  // RFC-4180 quoting: wrap + double interior quotes when the field carries a comma, quote or newline.
  if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build an RFC-4180 CSV and trigger a browser download. `headers` is the column row; `rows` are the data
 * rows (each a row of string|number cells, same arity as headers). Money cells should already be formatted
 * via money() (see file header).
 */
export function downloadCsv(
  filename: string,
  headers: string[],
  rows: Array<Array<string | number>>,
): void {
  const lines = [headers, ...rows].map((row) => row.map(csvField).join(','));
  // BOM so Excel reads UTF-8 (the £ sign) correctly; CRLF row terminator per RFC-4180.
  const csv = `﻿${lines.join('\r\n')}\r\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has dispatched before the URL is freed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** A filesystem-safe, date-stamped CSV filename (mirrors gdpr-download.ts exportFilename, .csv ext). */
export function exportCsvFilename(prefix: string): string {
  const slug =
    prefix
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'report';
  const stamp = new Date().toISOString().slice(0, 10);
  return `salonomy-${slug}-${stamp}.csv`;
}
